import { useRef, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useNotify } from "react-admin";
import dataProvider from "server/dataProvider";
import { doctoralRequests } from "api/doctoralApi";
import { getPermissions } from "auth/security/securityUtils";
import { getUserData } from "server/security/utils";
import { useNotifyError } from "utils/useNotifyError";
import { formatDoctoralDate } from "../../Partials/DoctoralDateField";

const MIN_GRADE = 2;
const MAX_GRADE = 6;

const KIND = { Discipline: 1, Language: 2 };
const STAGE = { written: 1, oral: 2 };

const PASS_AVERAGE = 5.0;

const fmt = v => (v === null || v === undefined ? "—" : Number(v).toFixed(2));

const disciplineResultChip = block => {
  if (block.finalAverage !== null && block.finalAverage !== undefined) {
    return Number(block.finalAverage) >= PASS_AVERAGE ? (
      <Chip
        size="small"
        color="success"
        label="Успешно"
      />
    ) : (
      <Chip
        size="small"
        color="error"
        label="Неуспешно"
      />
    );
  }
  if (block.writtenAverage !== null && block.writtenAverage !== undefined && !block.oralUnlocked)
    return (
      <Chip
        size="small"
        color="error"
        label="Неуспешно"
      />
    );
  return (
    <Chip
      size="small"
      label="В процес"
    />
  );
};

const members = block => [
  { role: "Председател", userId: block.chairUserId },
  { role: "Член 1", userId: block.member1UserId },
  { role: "Член 2", userId: block.member2UserId },
];

const ExamGradeGrid = ({ campaignId, candidacyId, kind, disciplineId, block, locked = false, onSaved }) => {
  const notifyError = useNotifyError();
  const notify = useNotify();
  const [savingKey, setSavingKey] = useState(null);
  const inputRefs = useRef({});

  if (!block) return null;

  if (!block.hasCommittee)
    return (
      <Typography
        variant="body2"
        sx={{ color: "text.secondary" }}
      >
        Изпитната комисия още не е определена.
      </Typography>
    );

  const currentUserId = getUserData()?.userId;
  const isAdmin = getPermissions()?.isAdmin === true;

  const examHeld = block.examDate ? new Date(block.examDate).getTime() <= Date.now() : false;

  const memberGrade = (userId, stage) => {
    const row = block.memberGrades?.find(g => g.memberUserId === userId);
    return row ? row[stage] : null;
  };

  const canEditMember = member =>
    !locked && block.canGrade && (isAdmin || (member.userId && member.userId === currentUserId));

  const saveGrade = async (member, stage) => {
    const key = `${member.userId}-${stage}`;
    const rawValue = inputRefs.current[key]?.value;
    const value = Number(rawValue);
    if (rawValue === "" || rawValue === undefined || Number.isNaN(value)) {
      notify("Въведете оценка преди запазване", { type: "warning" });
      return;
    }
    if (value < MIN_GRADE || value > MAX_GRADE) {
      notify(`Оценката трябва да е между ${MIN_GRADE.toFixed(2)} и ${MAX_GRADE.toFixed(2)}`, { type: "warning" });
      return;
    }

    setSavingKey(key);
    try {
      await dataProvider.customRequest(
        doctoralRequests.enterExamGrade(campaignId),
        {
          doctorantCandidacyId: candidacyId,
          kind: KIND[kind],
          doctoralDisciplineId: disciplineId,
          stage: STAGE[stage],
          grade: value,
          memberUserId: member.userId,
        },
        "POST",
      );
      notify("Оценката е записана", { type: "success" });
      if (onSaved) await onSaved();
    } catch (err) {
      notifyError(err);
    } finally {
      setSavingKey(null);
    }
  };

  const gradeCell = (member, stage) => {
    const key = `${member.userId}-${stage}`;
    const stageLocked = !examHeld || (stage === "oral" && !block.oralUnlocked);
    const editable = canEditMember(member) && !stageLocked;
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
        <TextField
          size="small"
          type="number"
          inputRef={
            editable
              ? el => {
                  inputRefs.current[key] = el;
                }
              : undefined
          }
          defaultValue={memberGrade(member.userId, stage) ?? ""}
          disabled={!editable || savingKey === key}
          inputProps={{ min: MIN_GRADE, max: MAX_GRADE, step: 0.25, style: { width: 64 } }}
        />
        {editable && (
          <Tooltip title="Запази оценката">
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={savingKey === key}
                onClick={() => saveGrade(member, stage)}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {kind === "Discipline" && <Box sx={{ mb: 1 }}>{disciplineResultChip(block)}</Box>}
      {!block.canGrade && (
        <Typography
          variant="body2"
          sx={{ mb: 1, color: "text.secondary" }}
        >
          {kind === "Language"
            ? "Оценяването е достъпно след датата на изпита по език."
            : "Оценяването е достъпно след утвърждаване на изпитната комисия."}
        </Typography>
      )}
      {block.canGrade && !examHeld && (
        <Typography
          variant="body2"
          sx={{ mb: 1, color: "text.secondary" }}
        >
          Оценяването се отключва след провеждане на изпита
          {block.examDate ? ` (${formatDoctoralDate(block.examDate, true)})` : ""}.
        </Typography>
      )}
      <Table size="small">
        <TableHead
          sx={{
            backgroundColor: "black",
            "& .MuiTableCell-root": {
              backgroundColor: "#5a5c9e87",
              color: "#fff",
              fontWeight: 700,
              fontSize: 12,
            },
          }}
        >
          <TableRow>
            <TableCell>Член на комисията</TableCell>
            <TableCell align="center">Писмен изпит</TableCell>
            <TableCell align="center">Устен изпит</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members(block).map((member, idx) => (
            <TableRow key={member.userId ?? idx}>
              <TableCell>
                {member.role}
                {member.userId && member.userId === currentUserId ? " (Вие)" : ""}
              </TableCell>
              <TableCell align="center">{gradeCell(member, "written")}</TableCell>
              <TableCell align="center">{gradeCell(member, "oral")}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ display: "flex", gap: 3, mt: 1, flexWrap: "wrap" }}>
        <Typography variant="body2">
          Среден писмен: <strong>{fmt(block.writtenAverage)}</strong>
        </Typography>
        <Typography variant="body2">
          Среден устен: <strong>{fmt(block.oralAverage)}</strong>
        </Typography>
        <Typography variant="body2">
          Общ успех: <strong>{fmt(block.finalAverage)}</strong>
        </Typography>
        {!block.oralUnlocked && (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary" }}
          >
            Устният изпит се отключва при среден писмен успех над 4.50.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ExamGradeGrid;
